import { useEffect, useMemo, useState } from 'react';

/**
 * Types
 */
type ListLimitStates = {
  limit: number;
  isLimited: boolean;
};
type ListLimitOptions = {
  init?: number;
  step?: number;
};
export type ListLimitActions = {
  reset: () => void;
  increase: (n?: number) => void;
};
type ListLimitReturn = [data: any[], states: ListLimitStates, actions: ListLimitActions];

/**
 * Initials
 */
export const defaultMax = 12;
const defaultMaxStep = 12;

/**
 * Hook: useListLimit
 */
export default function useListLimit(list: any[] = [], resetOn: any[] = [], options: ListLimitOptions = {}): ListLimitReturn {
  const { init = defaultMax, step = defaultMaxStep } = options;
  const deps = useMemo(() => [init, step, ...resetOn], [init, step, resetOn]);

  const [limit, setLimit] = useState(init);
  const currentLimit = useMemo(() => (list.length < limit ? list.length : limit), [list, limit]);
  const isLimited = !currentLimit || currentLimit >= list.length;
  const data = useMemo(() => (isLimited ? list : list.slice(0, limit)), [isLimited, limit, list]);
  const reset = () => setLimit(init);
  const increase = (n = step) => setLimit((d) => d + n);

  useEffect(() => {
    setLimit(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return [data, { limit: currentLimit, isLimited }, { reset, increase }];
}
